import type { CityConfig } from "../city-template/types";

export const doverConfig: CityConfig = {
  city: "Dover",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "dover",
  route: "/cities/dover",
  title: "Emergency Passport Dover, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Dover, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Strafford County and Seacoast travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Dover, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Dover and Strafford County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Dover%20New%20Hampshire%20downtown%20view%20with%20historic%20Washington%20Street%20district%20and%20the%20Cocheco%20River%2C%20charming%20brick%20mill%20buildings%20converted%20to%20modern%20shops%20and%20cafes%2C%20warm%20New%20England%20light%20with%20golden%20hour%20sky%2C%20vibrant%20small%20town%20scene%2C%20clean%20and%20inviting%20urban%20landscape%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20soft%20earth%20tones%20and%20warm%20amber%20sky%2C%20photorealistic%20cityscape&width=1800&height=700&seq=dover-nh-hero-001&orientation=landscape",
  heroImageAlt: "Dover New Hampshire historic downtown Washington Street district with the Cocheco River and charming brick mill buildings",
  geoPosition: "43.1979;-70.8737",
  geoPlacename: "Dover, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Customs House St, Boston, MA 02103",
    distance: "Located in downtown Boston — approximately 60–75 minutes from Dover via I-95 and I-93",
  },
  nearbyCitiesZips: [
    { name: "Dover", zips: "03820–03822" },
    { name: "Rochester", zips: "03839–03867" },
    { name: "Somersworth", zips: "03878" },
    { name: "Durham", zips: "03824" },
    { name: "Newmarket", zips: "03857" },
    { name: "Portsmouth", zips: "03801–03804" },
    { name: "Rollinsford", zips: "03869" },
    { name: "Madbury", zips: "03823" },
    { name: "Lee", zips: "03861" },
    { name: "Barrington", zips: "03825" },
    { name: "Strafford", zips: "03884" },
    { name: "Farmington", zips: "03835" },
    { name: "Epping", zips: "03042" },
    { name: "Hampton", zips: "03842" },
    { name: "Exeter", zips: "03833" },
    { name: "Greenland", zips: "03840" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Strafford County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/cities/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Concord", route: "/cities/concord-nh" },
    { name: "Portsmouth", route: "/cities/portsmouth" },
    { name: "Keene", route: "/cities/keene" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Laconia", route: "/cities/laconia" },
    { name: "Lebanon", route: "/cities/lebanon" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Dover remotely via secure courier. We do not have a walk-in office in Dover — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Strafford County travelers.",
  fpcOfficeDistance: "Serving all of Strafford and Rockingham Counties",
  serviceAreaIntro: "From Dover's historic downtown Washington Street to the Cocheco Riverfront, from the North Dover neighborhood to the Rollinsford line — Fast Passport Center serves the entire Dover community. We regularly help travelers from Rochester, Somersworth, Durham, and Newmarket get their passports fast.",
  nearbyCitiesIntro: "We serve all Dover neighborhoods and nearby Strafford County cities including Rochester, Somersworth, Durham, Newmarket, Portsmouth, Rollinsford, Madbury, Lee, Barrington, Strafford, Farmington, and Epping. If you are in the greater Dover area, we have you covered.",
  howItWorksIntro: "Most Dover travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Portsmouth International Airport (PSM) and Manchester-Boston Regional (MHT) both within reach, Strafford County travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Dover and Strafford County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Dover? Here are the most common ones we hear from Strafford County travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Dover international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Christine M.",
      location: "Dover, NH",
      rating: 5,
      text: "My business trip to Toronto was in 4 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my trip.",
      service: "Emergency Passport",
    },
    {
      name: "Joseph R.",
      location: "Rochester, NH",
      rating: 5,
      text: "Needed to renew for a family vacation to Europe. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Amanda B.",
      location: "Somersworth, NH",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Kevin D.",
      location: "Durham, NH",
      rating: 5,
      text: "As a UNH student studying abroad, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "dover-1",
      name: "Dover Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "133 Washington St",
      city: "Dover",
      state: "NH",
      zip: "03820",
      phone: "(603) 742-2064",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Dover",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Dover. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-2",
      name: "Rochester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "70 Main St",
      city: "Rochester",
      state: "NH",
      zip: "03867",
      phone: "(603) 332-4805",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Strafford County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-3",
      name: "Somersworth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2 Government Way",
      city: "Somersworth",
      state: "NH",
      zip: "03878",
      phone: "(603) 692-4004",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.5 miles from downtown Somersworth",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Somersworth and Rollinsford residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-4",
      name: "Durham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 Dover Rd",
      city: "Durham",
      state: "NH",
      zip: "03824",
      phone: "(603) 868-5043",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from UNH campus",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for UNH students and faculty. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-5",
      name: "Newmarket Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "126 Main St",
      city: "Newmarket",
      state: "NH",
      zip: "03857",
      phone: "(603) 659-3331",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.3 miles from downtown Newmarket",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Strafford County near the Rockingham border. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "dover-6",
      name: "Dover City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "288 Central Ave",
      city: "Dover",
      state: "NH",
      zip: "03820",
      phone: "(603) 516-6000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from City Hall",
      website: "https://www.dover.nh.gov/",
      websiteLabel: "Dover City Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Dover photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Strafford County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Dover applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Dover families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Dover travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Dover." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Dover address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Dover before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Dover for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Strafford County travelers." },
    { q: "How fast can I renew my passport in Dover?", a: "Fast Passport Center offers fast passport renewal in Dover in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Dover in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Dover?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Dover residents." },
    { q: "Can I expedite a child passport in Dover?", a: "Yes. Fast Passport Center expedites child passports in Dover for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Dover with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Dover?", a: "With Fast Passport Center's emergency courier service, Dover travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Dover?", a: "Popular options include the Dover Post Office (03820), Rochester Post Office (03867), Somersworth Post Office (03878), Durham Post Office (03824), and Dover City Clerk (03820). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Dover?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Rochester or Somersworth?", a: "We serve all of Strafford County including Rochester, Somersworth, Durham, Newmarket, Portsmouth, Rollinsford, Madbury, Lee, Barrington, and Strafford. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Dover?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Dover", address: "892 Central Ave, Dover, NH 03820", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Dover", address: "550 Central Ave, Dover, NH 03820", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Dover", address: "34 Dover Point Rd, Dover, NH 03820", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Dover", address: "116 Washington St, Dover, NH 03820", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Dover", address: "46 Boston Harbor Rd, Dover, NH 03820", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Rochester", address: "72 Main St, Rochester, NH 03867", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Somersworth", address: "2 Government Way, Somersworth, NH 03878", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Dover", address: "11 New Rochester Rd, Dover, NH 03820", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Dover", address: "50 Boston Harbor Rd, Dover, NH 03820", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Rochester", address: "22 Main St, Rochester, NH 03867", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Somersworth", address: "1 Government Way, Somersworth, NH 03878", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Exeter", address: "9 Columbia Cir, Exeter, NH 03833", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for coastal New Hampshire residents.", featured: true },
  ],
  airports: [
    { code: "PSM", name: "Portsmouth International Airport", distance: "10 miles from downtown Dover", address: "36 New Hampshire Ave, Portsmouth, NH 03801", note: "Regional airport with some international flights. Convenient for Strafford County travelers.", flightUrl: "https://www.flyportsmouth.com/", flightLabel: "Portsmouth Airport", featured: true },
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "40 miles from Dover", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major commercial airport for Dover travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "60 miles from Dover", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-95.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
};